import { useEffect, useMemo, useState } from "react";
import { Box, LinearProgress, Stack, Typography } from "@mui/material";

// zxcvbn-ts is ~150KB (mostly the English wordlist). We dynamic-import
// it once on first need so the landing page's initial bundle stays
// lean. The promise is cached at module scope, so every subsequent
// PasswordStrengthMeter mount resolves instantly.
type ZxcvbnFn = (
  password: string,
  userInputs?: (string | number)[]
) => {
  score: 0 | 1 | 2 | 3 | 4;
  feedback: { warning: string; suggestions: string[] };
};

let zxcvbnPromise: Promise<ZxcvbnFn> | null = null;
let cachedZxcvbn: ZxcvbnFn | null = null;

function loadZxcvbn(): Promise<ZxcvbnFn> {
  if (cachedZxcvbn) return Promise.resolve(cachedZxcvbn);
  if (!zxcvbnPromise) {
    zxcvbnPromise = (async () => {
      const [core, common, en] = await Promise.all([
        import("@zxcvbn-ts/core"),
        import("@zxcvbn-ts/language-common"),
        import("@zxcvbn-ts/language-en"),
      ]);
      core.zxcvbnOptions.setOptions({
        translations: en.translations,
        graphs: common.adjacencyGraphs,
        dictionary: {
          ...common.dictionary,
          ...en.dictionary,
        },
      });
      cachedZxcvbn = core.zxcvbn as ZxcvbnFn;
      return cachedZxcvbn;
    })();
  }
  return zxcvbnPromise;
}

interface Props {
  password: string;
  /**
   * Optional context fed to zxcvbn as "user inputs" — values it should
   * treat as guessable when present inside the password (email, name,
   * username). Improves the catch rate on passwords like "JohnSmith2026".
   */
  userInputs?: (string | number | undefined | null)[];
  /**
   * If true, drop the right-hand hint text and render only the bar +
   * level. Defaults to false.
   */
  compact?: boolean;
}

interface Strength {
  score: 0 | 1 | 2 | 3 | 4;
  label: string;
  color: "error" | "warning" | "success";
  hint: string;
}

const LABELS = [
  "Very weak",
  "Weak",
  "Fair",
  "Good",
  "Strong",
] as const;

const BAR_COUNT = 4;

function score(
  zxcvbn: ZxcvbnFn,
  password: string,
  userInputs: (string | number)[]
): Strength {
  const result = zxcvbn(password, userInputs);
  const s = result.score;
  // s is 0..4 from zxcvbn's contract; the tuple is the same length,
  // so the lookup is always defined — assert for noUncheckedIndexedAccess.
  const label = LABELS[s]!;
  const color: Strength["color"] =
    s <= 1 ? "error" : s === 2 ? "warning" : "success";
  // zxcvbn surfaces the *most* specific feedback in `warning`; falls
  // back to a less-specific suggestion. When the password is already
  // strong, both are empty — leave the hint blank.
  const hint =
    result.feedback.warning?.trim() ||
    result.feedback.suggestions?.[0]?.trim() ||
    "";
  return { score: s, label, color, hint };
}

const PasswordStrengthMeter = ({
  password,
  userInputs,
  compact = false,
}: Props) => {
  // The initializer must be wrapped: useState treats a bare function
  // argument as a lazy initializer and calls it. Passing the cached
  // zxcvbn directly meant that once the module cache was warm, the next
  // mount invoked zxcvbn() with no password and threw.
  const [zxcvbn, setZxcvbn] = useState<ZxcvbnFn | null>(() => cachedZxcvbn);

  useEffect(() => {
    if (zxcvbn) return;
    let cancelled = false;
    loadZxcvbn()
      .then((fn) => {
        if (!cancelled) setZxcvbn(() => fn);
      })
      .catch(() => {
        // Network or chunk-load failure — leave the meter hidden
        // rather than degrading to a misleading heuristic.
      });
    return () => {
      cancelled = true;
    };
  }, [zxcvbn]);

  // Normalize the optional userInputs array (drop nullish, coerce to
  // strings) before zxcvbn sees it.
  const normalizedInputs = useMemo<(string | number)[]>(
    () =>
      (userInputs ?? [])
        .filter((v): v is string | number => v !== undefined && v !== null)
        .map((v) => (typeof v === "number" ? v : String(v).trim()))
        .filter((v) => v !== ""),
    [userInputs]
  );

  const strength = useMemo<Strength | null>(() => {
    if (!zxcvbn || password.length === 0) return null;
    return score(zxcvbn, password, normalizedInputs);
  }, [zxcvbn, password, normalizedInputs]);

  // Keep the form visually quiet when the field is empty OR before
  // zxcvbn has finished loading. The library typically lands inside
  // the time it takes to type the first character — most users will
  // never see the empty window.
  if (!strength) return null;

  const { score: s, label, color, hint } = strength;

  return (
    <Box
      role="status"
      aria-live="polite"
      aria-label={`Password strength: ${label}`}
      sx={{ mt: -1 }}
    >
      <Stack
        direction="row"
        spacing={0.5}
        sx={{ mb: 0.75 }}
        aria-hidden="true"
      >
        {Array.from({ length: BAR_COUNT }).map((_, i) => {
          const filled = i < s;
          return (
            <LinearProgress
              key={i}
              variant="determinate"
              value={filled ? 100 : 0}
              color={color}
              sx={{
                flex: 1,
                height: 4,
                borderRadius: 2,
                backgroundColor: (t) => t.palette.action.disabledBackground,
              }}
            />
          );
        })}
      </Stack>
      <Stack
        direction="row"
        spacing={1}
        justifyContent="space-between"
        alignItems="baseline"
      >
        <Typography
          variant="caption"
          color={`${color}.main`}
          sx={{ fontWeight: 600 }}
        >
          {label}
        </Typography>
        {!compact && hint && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ textAlign: "right" }}
          >
            {hint}
          </Typography>
        )}
      </Stack>
    </Box>
  );
};

export default PasswordStrengthMeter;
